import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ── Visite guidée "spotlight" ─────────────────────────────────────
// Déroulé première connexion : login → Overview (statistiques) → explication
// de chaque fonctionnalité (KPIs, camemberts, AI Launcher, Skills IA, règles,
// tracking) → fin sur Plateformes connectées pour brancher le premier compte.
//
// Chaque étape cible un élément réel de l'UI via [data-tour="…"] : l'élément
// est surligné (spotlight découpé dans un voile sombre) avec une bulle
// d'explication. Si la cible n'existe pas (ex: sidebar masquée sur mobile),
// la bulle s'affiche centrée — le contenu reste le même.
//
// Relançable à tout moment via le bouton flottant "?" (Dashboard.jsx).

export const TOUR_KEY = 'oba_tour_done';

// ── Parcours SHOPIFY (marchand) ───────────────────────────────────
// Overview → connecter son store Shopify → connecter ses comptes ads →
// tracking (clics & événements du store) → Shopify Ads (créa produit par IA).
const SHOPIFY_STEPS = [
  {
    id: 'welcome',
    center: true,
    emoji: '🛍️',
    title: 'Bienvenue sur OneBotAds !',
    text: "Votre boutique, vos pubs, au même endroit. Suivez cette visite d'une minute : connexion de votre store, de vos comptes publicitaires, tracking des ventes et création de pubs produit par IA.",
    route: '/dashboard/statistics',
  },
  {
    id: 'overview',
    target: 'stats-kpis',
    route: '/dashboard/statistics',
    emoji: '📊',
    title: "Votre vue d'ensemble",
    text: 'Dépenses, ROAS, conversions, CPA… tous les KPIs de vos campagnes, toutes plateformes confondues. Chaque matin, vous savez en 10 secondes si vos pubs sont rentables.',
  },
  {
    id: 'store',
    target: 'stores-page',
    route: '/dashboard/stores',
    emoji: '🛒',
    title: 'Connectez votre store Shopify',
    text: "Première étape : reliez votre boutique. OneBotAds récupère vos produits (titres, prix, images) pour créer des pubs en 1 clic et suivre vos ventes.",
  },
  {
    id: 'platforms',
    target: 'platforms-page',
    route: '/dashboard/connected-platforms',
    emoji: '🔗',
    title: 'Connectez vos comptes publicitaires',
    text: 'Meta, TikTok, Google, Snapchat, LinkedIn — reliez les régies où vous diffusez (connexion sécurisée OAuth). Vos campagnes et leurs statistiques remontent automatiquement.',
  },
  {
    id: 'tracking',
    target: 'nav:/dashboard/tracking',
    route: '/dashboard/statistics',
    emoji: '🎯',
    title: 'Tracking : clics & événements de votre store',
    text: 'Visualisez chaque clic, ajout au panier et achat sur votre boutique, attribué à la bonne pub. Vous savez précisément quelle campagne génère vos ventes.',
  },
  {
    id: 'shopify-ads',
    target: 'nav:/dashboard/shopify-ads',
    emoji: '⚡',
    title: 'Shopify Ads : la pub produit en 1 clic',
    text: "Choisissez un produit de votre boutique : Alex pré-remplit la campagne et vous recommande un prompt de créa (image ou vidéo) adapté au produit et à l'étape de votre plan média.",
  },
  {
    id: 'go-store',
    target: 'stores-page',
    route: '/dashboard/stores',
    emoji: '🚀',
    title: 'Commencez par votre boutique',
    text: 'Connectez votre store Shopify maintenant — le reste (produits, tracking, pubs) se met en place tout seul derrière.',
    lastLabel: 'Connecter mon store 🚀',
  },
];

// ── Parcours ADVERTISER (défaut) ──────────────────────────────────
// Overview → plateformes → AI Launcher → Cinema Studio.
const STEPS = [
  {
    id: 'welcome',
    center: true,
    emoji: '👋',
    title: 'Bienvenue sur OneBotAds !',
    text: "Suivez cette visite guidée d'une minute pour découvrir votre tableau de bord : statistiques, création de campagnes par IA, stratégies et connexion de vos comptes publicitaires.",
    route: '/dashboard/statistics',
  },
  {
    id: 'periods',
    target: 'stats-periods',
    route: '/dashboard/statistics',
    emoji: '📅',
    title: 'Choisissez votre période',
    text: "Aujourd'hui, hier, 7/30/90 jours, ce mois, mois dernier ou des dates personnalisées — toutes vos statistiques s'adaptent instantanément.",
  },
  {
    id: 'charts',
    target: 'stats-charts',
    route: '/dashboard/statistics',
    emoji: '🥧',
    title: 'Répartition par plateforme',
    text: "Ces camemberts montrent où part votre budget et d'où viennent vos conversions. Tant qu'aucune campagne ne diffuse, des données de démonstration illustrent le dashboard.",
  },
  {
    id: 'kpis',
    target: 'stats-kpis',
    route: '/dashboard/statistics',
    emoji: '📊',
    title: 'Vos KPIs de media buying',
    text: 'Dépenses, revenu, ROAS, conversions, CPA, CTR, CPC, CPM, vues vidéo, engagements… Les 15 indicateurs clés que tout advertiser suit, calculés sur la délivraison réelle de toutes vos campagnes.',
  },
  {
    id: 'platforms',
    target: 'platforms-page',
    route: '/dashboard/connected-platforms',
    emoji: '🔗',
    title: 'Connectez vos plateformes publicitaires',
    text: 'Meta, TikTok, Google, Snapchat, LinkedIn — reliez un compte publicitaire en 2 clics (connexion sécurisée OAuth). C\'est le point de départ de tout le reste.',
  },
  {
    id: 'ai-launcher',
    target: 'nav:/dashboard/create-ad-ai',
    emoji: '🤖',
    title: 'AI Launcher — Alex, votre media buyer IA',
    text: "Décrivez votre produit et Alex construit la campagne avec vous : audience, budget, objectif, créa générée par IA — puis il la lance sur la plateforme de votre choix.",
  },
  {
    id: 'skills',
    target: 'nav:/dashboard/skills',
    emoji: '🧠',
    title: 'Skills IA — les stratégies d\'Alex',
    text: "63 méthodologies d'expert (diagnostics, scaling, créa, funnel…) qu'Alex applique à vos vraies données. Ajoutez vos propres stratégies pour qu'il travaille à votre façon.",
  },
  {
    id: 'cinema',
    target: 'studio-hero',
    route: '/dashboard/cinema-studio',
    emoji: '🎬',
    title: 'Cinema Studio — vos pubs vidéo par IA',
    text: 'Générez images, vidéos, avatars et voix off publicitaires avec les meilleurs modèles IA. Inspirez-vous de la vitrine Marketing Studio : des exemples générés avec leur prompt, prêts à réutiliser.',
    lastLabel: 'Créer ma première créa 🎬',
  },
];

const findTarget = (target) => {
  if (!target) return null;
  const el = document.querySelector(`[data-tour="${target}"]`);
  // offsetParent null = élément masqué (ex: sidebar desktop sur mobile).
  if (!el || (el.offsetParent === null && getComputedStyle(el).position !== 'fixed')) return null;
  return el;
};

// variant: 'shopify' (marchand — journey Shopify) | 'default' (advertiser).
const GuidedTour = ({ onClose, variant = 'default' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const steps = variant === 'shopify' ? SHOPIFY_STEPS : STEPS;
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null); // null = bulle centrée
  const retryRef = useRef(null);

  const step = steps[stepIdx];

  // Positionne le spotlight sur la cible de l'étape courante (avec retries :
  // après un changement de route, l'élément peut mettre quelques centaines de
  // ms à apparaître).
  const locate = useCallback(() => {
    clearInterval(retryRef.current);
    // Efface le spotlight de l'étape précédente pendant la recherche de la
    // nouvelle cible — sinon l'ancien cadre reste affiché au mauvais endroit
    // (bulle centrée en attendant).
    setRect(null);
    if (step.center || !step.target) return;

    let tries = 0;
    const attempt = () => {
      const el = findTarget(step.target);
      if (el) {
        clearInterval(retryRef.current);
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // Laisse le scroll se terminer avant de mesurer.
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }, 350);
      } else if (++tries > 16) {
        clearInterval(retryRef.current);
        setRect(null); // fallback : bulle centrée
      }
    };
    attempt();
    retryRef.current = setInterval(attempt, 180);
  }, [step]);

  // Navigation vers la route de l'étape si nécessaire, puis localisation.
  // startsWith : certaines routes redirigent vers une sous-route canonique
  // (ex: /cinema-studio → /cinema-studio/text2image) — une égalité stricte
  // re-naviguerait en boucle.
  useEffect(() => {
    if (step.route && !location.pathname.startsWith(step.route)) {
      navigate(step.route);
    }
    const t = setTimeout(locate, 80);
    return () => { clearTimeout(t); clearInterval(retryRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, location.pathname]);

  // Recalcule à chaque resize.
  useEffect(() => {
    const onResize = () => locate();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [locate]);

  const finish = () => {
    // La dernière étape a déjà navigué vers sa page d'action finale (Cinema
    // Studio pour l'advertiser, Stores pour le marchand Shopify) — on laisse
    // l'utilisateur exactement là.
    localStorage.setItem(TOUR_KEY, 'true');
    onClose();
  };

  const next = () => (stepIdx < steps.length - 1 ? setStepIdx(stepIdx + 1) : finish());
  const prev = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const isLast = stepIdx === steps.length - 1;
  const pad = 6; // marge du spotlight autour de l'élément

  // Position de la bulle : sous la cible si la moitié haute, sinon au-dessus.
  const bubbleStyle = (() => {
    if (!rect) return null;
    const bubbleWidth = Math.min(340, window.innerWidth - 24);
    const below = rect.top + rect.height / 2 < window.innerHeight / 2;
    const top = below ? rect.top + rect.height + pad + 12 : undefined;
    const bottom = below ? undefined : window.innerHeight - rect.top + pad + 12;
    let left = rect.left + rect.width / 2 - bubbleWidth / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - bubbleWidth - 12));
    return { position: 'fixed', top, bottom, left, width: bubbleWidth };
  })();

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-label="Visite guidée">
      {/* Voile : soit un spotlight découpé autour de la cible, soit un voile plein */}
      {rect ? (
        <div
          className="absolute rounded-xl transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            boxShadow: '0 0 0 9999px rgba(17, 14, 50, 0.62)',
            border: '2px solid rgba(255,255,255,0.85)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-[#110E32]/70" />
      )}

      {/* Bulle d'explication */}
      <div
        className="bg-white rounded-2xl shadow-2xl p-4 md:p-5"
        style={bubbleStyle || {
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: Math.min(360, window.innerWidth - 24),
        }}
      >
        <div className="flex items-start gap-3 mb-2">
          <span className="text-2xl md:text-3xl flex-shrink-0">{step.emoji}</span>
          <div className="min-w-0">
            <h3 className="text-sm md:text-base font-bold text-[#221C51] leading-snug">{step.title}</h3>
          </div>
        </div>
        <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-4">{step.text}</p>

        {/* Progression */}
        <div className="flex items-center gap-1 mb-4">
          {steps.map((s, i) => (
            <span
              key={s.id}
              className={`h-1.5 rounded-full transition-all ${i === stepIdx ? 'w-5 bg-[#221C51]' : 'w-1.5 bg-gray-200'}`}
            />
          ))}
          <span className="ml-auto text-[10px] text-gray-400 font-semibold">{stepIdx + 1}/{steps.length}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => finish(false)}
            className="text-xs text-gray-400 hover:text-gray-600 px-2 py-2"
          >
            Passer
          </button>
          <div className="ml-auto flex items-center gap-2">
            {stepIdx > 0 && (
              <button
                onClick={prev}
                className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                ← Précédent
              </button>
            )}
            <button
              onClick={next}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-[#221C51] text-white hover:bg-[#2d2570] shadow-sm"
            >
              {isLast ? (step.lastLabel || 'Terminer') : stepIdx === 0 ? 'Commencer la visite →' : 'Suivant →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
